import { Link, NavLink } from "react-router-dom";
import { Bookmark, Boxes, LayoutDashboard, LogOut, Search, ShoppingBag, Ticket } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";

const navItems = (isAdmin) => [
  { to: "/", label: "Katalog", icon: Search, id: "nav-catalog" },
  { to: "/paket", label: "Paket", icon: Bookmark, id: "nav-templates" },
  { to: "/peminjaman", label: "Booking", icon: Ticket, id: "nav-bookings" },
  ...(isAdmin ? [{ to: "/admin", label: "Admin", icon: LayoutDashboard, id: "nav-admin" }] : []),
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { totalQty } = useCart();
  const items = navItems(user?.role === "admin");

  return (
    <div className="borrower-light min-h-screen bg-[#C2D6EB] text-slate-900">
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-3">
            <img src="/yn-logo.svg" alt="YukNgaji" className="brand-yn h-9 w-9 object-contain" />
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#007AFF]">
              <Boxes className="h-5 w-5 text-white" />
            </span>
            <span className="font-heading text-lg font-bold tracking-tight text-slate-900">Dexter</span>
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            {items.map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.to === "/"}
                data-testid={i.id}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                    isActive ? "bg-[#e7f1fc] text-[#004f9e]" : "text-slate-500 hover:text-slate-900"
                  }`
                }
              >
                {i.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/checkout" data-testid="cart-button"
              className="relative inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-lg border border-[#0067c8] bg-[#0067c8] px-3 text-sm font-semibold text-white transition-colors duration-200 hover:border-[#004f9e] hover:bg-[#004f9e]">
              <ShoppingBag className="h-5 w-5" />
              <span data-testid="cart-badge" className="rounded-full bg-white/25 px-2 py-0.5 text-xs font-bold tabular-nums">
                {totalQty}
              </span>
              <span className="sr-only">Keranjang, {totalQty} unit</span>
            </Link>
            <span data-testid="current-user-name" className="hidden text-sm text-slate-500 sm:block">
              {user?.name}
            </span>
            <Button
              data-testid="logout-button"
              variant="ghost"
              size="icon"
              className="rounded-full text-slate-500 hover:text-slate-900"
              onClick={async () => {
                await logout();
                window.location.replace("/login");
              }}
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 pt-8 [padding-bottom:calc(7rem+env(safe-area-inset-bottom,0px))] md:[padding-bottom:4rem]">{children}</main>

      <nav className="fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 [padding-bottom:env(safe-area-inset-bottom,0px)] backdrop-blur-xl md:hidden">
        <div className="flex">
          {items.map((i) => (
            <NavLink
              key={i.to}
              to={i.to}
              end={i.to === "/"}
              data-testid={`${i.id}-mobile`}
              className={({ isActive }) =>
                `flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium ${
                  isActive ? "text-[#0067c8]" : "text-slate-500"
                }`
              }
            >
              <i.icon className="h-5 w-5" />
              {i.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
